/**
 * J10 idle meditate (docs/design/2026-10-11-auto-play.md): 60 s with no input, in a safe zone and out of combat, the
 * character sits and meditates (MeditationSystem; cultivation pays the meditation rate through its boost ledger).
 * Any command or held control stands them up again. `enabled` is the client's setting (default on).
 */
import type { PlayerCommand } from '../world';
import { safePlaceOf } from './safeZones';
import type { SystemHost, WorldSystem } from './types';

export const IDLE_SIT = { seconds: 60 } as const;

export class IdleSitSystem implements WorldSystem {
  readonly name = 'idleSit';
  /** The client's idleSit setting. */
  enabled = true;
  /** Seconds without input. */
  idle = 0;
  /** Seconds the character has sat by this rule (0 when not sitting by it). */
  sat = 0;
  private sitting = false;
  constructor(private readonly h: SystemHost) {}

  get sittingIdle(): boolean {
    return this.sitting;
  }

  /** Any input at all (the client also calls this for keys and touches that make no command). */
  poke(): void {
    this.idle = 0;
    if (this.sitting) {
      this.sitting = false;
      this.sat = 0;
      this.h.stopMeditating();
    }
  }

  refuse(cmd: PlayerCommand): boolean {
    const was = this.sitting;
    this.poke();
    return was && cmd.t === 'meditate'; // the press that stands them up is not also a new sit
  }

  step(dt: number): void {
    const w = this.h.world;
    const p = w.player;
    const i = this.h.input;
    if (this.sitting && !p.meditating) {
      this.sitting = false; // stood up by another rule
      this.sat = 0;
    }
    if (i.dx || i.dy || i.block || i.fly || i.engage || p.moving || w.auto.on || w.dance.session) this.idle = 0; // a ring in progress is play, not idling
    if (this.sitting) {
      this.sat += dt;
      return;
    }
    if (!this.enabled) {
      this.idle = 0;
      return;
    }
    if (p.meditating || p.dead || p.combatT > 0 || w.duel || w.inArena() || safePlaceOf(w) === null) {
      this.idle = 0;
      return;
    }
    if ((this.idle += dt) < IDLE_SIT.seconds) return;
    this.h.world.apply({ t: 'meditate' });
    if (p.meditating) this.sitting = true;
    else this.idle = 0;
  }
}
